// Solar Calculator Section Component
import { useState, useEffect, useCallback } from 'react';
import {
    FiHome,
    FiBattery,
    FiDollarSign,
    FiZap,
} from 'react-icons/fi';
import {
    MdSolarPower,
    MdPhone,
    MdEmail,
    MdApartment,
    MdFactory,
    MdRoofing,
    MdOutlineSquareFoot,
    MdGridOn,
    MdElectricalServices,
    MdElectricMeter,
    MdLandscape,
} from 'react-icons/md';

const SolarCalculatorSection = ({ setIsSolarDataValid, onDataUpdate, initialData }) => {
    // Initialize state with initialData if it exists
    // Format numbers with spaces as thousands separator (e.g. 10 000, 271 575)
    const fmtNum = (n) => Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

    const [monthlyBill, setMonthlyBill] = useState(initialData?.monthlyBill || 1500);
    const [monthlyUnits, setMonthlyUnits] = useState(initialData?.monthlyUnits || 600); // kWh
    const [roofSize, setRoofSize] = useState(initialData?.roofSize || 100);
    // Eswatini annual average peak sun hours — fixed, not exposed to the client
    const sunHours = 5.5;
    const [systemType, setSystemType] = useState(initialData?.systemType || 'hybrid');
    const [batteryBackup, setBatteryBackup] = useState(initialData?.batteryBackup || 'yes');
    const [backupHours, setBackupHours] = useState(initialData?.backupHours || 4); // Hours of backup needed
    const [roofType, setRoofType] = useState(initialData?.roofType || 'metal');
    const [roofPitch, setRoofPitch] = useState(initialData?.roofPitch || 'medium');
    const [maintenancePlan, setMaintenancePlan] = useState(initialData?.maintenancePlan || 'basic');
    const [propertyType, setPropertyType] = useState(initialData?.propertyType || 'residential');
    const [desiredSystemSizeKW, setDesiredSystemSizeKW] = useState(initialData?.desiredSystemSizeKW || null);
    // 'auto' = bill-based, 'roofMax' = fill roof, 'manual' = user-entered kW
    const [systemSizeMode, setSystemSizeMode] = useState(initialData?.desiredSystemSizeKW ? 'manual' : 'auto');
    const [showCalcDetails, setShowCalcDetails] = useState(false);

    // String display states for free-form number inputs (prevents 0-prefix and backspace issues)
    const [roofSizeInput, setRoofSizeInput] = useState(String(initialData?.roofSize || 100));
    const [desiredSizeInput, setDesiredSizeInput] = useState(
        String(initialData?.desiredSystemSizeKW ?? '')
    );

    // Calculate monthly units from bill if not provided
    useEffect(() => {
        const costPerKwh = 1.3; // SEC Eswatini blended residential/commercial rate E/kWh
        const calculatedUnits = monthlyBill / costPerKwh;
        setMonthlyUnits(Math.round(calculatedUnits));
    }, [monthlyBill]);

    // Keep desiredSystemSizeKW in sync with the live roof max when roofMax mode is active
    useEffect(() => {
        if (systemSizeMode !== 'roofMax') return;
        const dcAcRatio = systemType === 'grid-tie' ? 1.5 : 1.0;
        const maxPanels = Math.floor(roofSize / 2);
        const maxKW = Math.round(((maxPanels * 600) / dcAcRatio / 1000) * 10) / 10;
        setDesiredSystemSizeKW(maxKW);
        setDesiredSizeInput(String(maxKW));
    }, [systemSizeMode, roofSize, systemType]);

    // Memoize the calculateSolarSystem function with useCallback
    const calculateSolarSystem = useCallback(() => {
        // Industry-standard calculations based on your guidelines
        const dailyUsageKwh = monthlyUnits / 30;

        // Calculate Possible System Size based on energy needs (what customer needs)
        const requiredSystemSizeKW = dailyUsageKwh / sunHours; // Use actual sun hours for consistency

        // Adjust for efficiency losses (typically 15-20%)
        const requiredAdjustedSystemSizeKW = requiredSystemSizeKW * 1.15;

        // Calculate MAXIMUM system size based on available roof space
        // Standard panel: 600W ≈ 2m²
        // IMPORTANT: maxSystemSizeByRoof must be expressed in AC kW.
        // For grid-tie (DC:AC = 1.5), the panels produce 1.5× the AC inverter rating,
        // so the AC capacity from a given roof is: (panels × 600W) / dcAcRatio / 1000.
        // This ensures the clamp on actualSystemSizeKW (AC kW) is consistent with how
        // panels are later counted: panels = actualSystemSizeKW × dcAcRatio × 1000 / 600.
        const dcAcRatio = systemType === 'grid-tie' ? 1.5 : 1.0;
        const maxPanelsByRoof = Math.floor(roofSize / 2);
        const maxSystemSizeByRoof = (maxPanelsByRoof * 600) / dcAcRatio / 1000;

        // Determine ACTUAL system size we'll install
        // For quoting: Install what's NEEDED, not what's POSSIBLE
        const autoSystemSizeKW = Math.min(requiredAdjustedSystemSizeKW, maxSystemSizeByRoof);
        // Allow user to override with a custom size — do NOT clamp to roof max here;
        // if the desired size exceeds the roof, the roof warning banner handles it.
        const actualSystemSizeKW = desiredSystemSizeKW ?? autoSystemSizeKW;

        // Calculate panels needed for ACTUAL system
        // Grid-tie systems use a 1.5 DC:AC ratio (industry standard) — the inverter clips at peak
        // but harvests far more energy during mornings, evenings, and overcast hours.
        // Hybrid and off-grid use a 1.0 ratio (battery absorbs all generation, so no clipping benefit).
        // dcAcRatio is already declared above (used in maxSystemSizeByRoof).
        const actualPanels = Math.ceil((actualSystemSizeKW * 1000 * dcAcRatio) / 600);

        // Calculate how much roof space is USED vs AVAILABLE
        const usedRoofSpace = actualPanels * 2; // Each panel uses ~2m² (600W panel footprint)
        const roofUsagePercentage = roofSize > 0 ? (usedRoofSpace / roofSize) * 100 : 0;

        // Determine if system is ROOF-LIMITED or ENERGY-LIMITED
        const isRoofLimited = maxSystemSizeByRoof < requiredAdjustedSystemSizeKW;
        const isEnergyLimited = !isRoofLimited; // Roof has more space than needed

        const extraPanelsPossible = maxPanelsByRoof - actualPanels;
        const extraRoofSpaceAvailable = roofSize - usedRoofSpace;

        // ─── Cost Calculations ──────────────────────────────────────────────
        // Based on real BOQ (110kW FTM Factory, 264×625W) using Segen SA prices + 35% markup
        // Labour rate: E2,800/day  |  Panels: 600W Tier-1 Mono  |  ZAR ≈ SZL

        // 1. Solar Panels — 600W Tier-1 mono (JA/Jinko/Canadian via Segen SA)
        //    BOQ: 625W panels @ E2,300/panel — applied to actual panel count (which already
        //    reflects DC oversizing). Using panels × unit price is correct; per-watt on AC kW
        //    would undercount because DC capacity = AC × 1.5 for grid-tie.
        const panelUnitCost = 2300;
        const panelCost = actualPanels * panelUnitCost;

        // 2. Inverter (Solis S5/S6 via Segen: R108,000/110kW = R982/kW × 1.35 = R1,325/kW)
        let inverterType = '';
        let inverterCostPerKW = 0;
        switch (systemType) {
            case 'grid-tie':
                inverterType = 'Grid-Tie Inverter';
                inverterCostPerKW = 982; // Solis S6 grid-tie: BOQ E108,000 ÷ 110kW = E981.82/kW
                break;
            case 'hybrid':
                inverterType = 'Hybrid Inverter';
                inverterCostPerKW = 1800; // Includes battery management circuitry premium
                break;
            case 'off-grid':
                inverterType = 'Off-Grid Inverter';
                inverterCostPerKW = 2000; // Full off-grid premium
                break;
            default:
                inverterType = 'Hybrid Inverter';
                inverterCostPerKW = 1800;
                break;
        }
        const inverterCost = actualSystemSizeKW * inverterCostPerKW;

        // 3. Batteries (if required)
        let batteryCost = 0;
        let actualBatterySize = 0;
        if (batteryBackup === 'yes' && (systemType === 'hybrid' || systemType === 'off-grid')) {
            // When the user has manually chosen a system size, size the battery to that
            // system's output — not to their bill consumption. This keeps the quote stable
            // when the bill changes but the desired system size is locked.
            // When in auto mode, use actual daily consumption from the bill as usual.
            const batteryDailyKwh = desiredSystemSizeKW
                ? actualSystemSizeKW * sunHours * 0.85  // system's daily output
                : dailyUsageKwh;                         // bill-derived consumption
            // Average load (kW) × backup hours = kWh of energy needed
            // ×1.2 safety margin, ÷0.8 usable depth-of-discharge for LiFePO4
            const avgLoadKw = batteryDailyKwh / 24;
            const backupEnergyKwh = avgLoadKw * backupHours;
            actualBatterySize = Math.ceil((backupEnergyKwh * 1.2) / 0.8);
            // Battery units: 5kWh each @ E5,000/unit — client scales by adding more units
            const batteryUnitKwh = 5;
            const batteryUnitCost = 5000;
            const batteryUnitCount = Math.ceil(actualBatterySize / batteryUnitKwh);
            batteryCost = batteryUnitCount * batteryUnitCost;
        }

        // 4. Mounting + DC Hardware (IBR rails, clamps, DC combiner box, PV cables, MC4 connectors)
        //    BOQ breakdown (264 panels):
        //      Structural (rails, clamps, screws, combiner, MC4): 191,681 ÷ 264 = R726/panel
        //      DC cables (Red+Black 6mm PV cable): 144,000 ÷ 264 = R545/panel
        //      Total: R1,272/panel
        //    Roof-type factor: Metal 1.0 | Concrete 1.1 | Tile 1.2 | Ground 1.3
        // Mounting: fixed base (scaffolding/setup) + per-panel hardware (rails, clamps, screws)
        //   Cross-check: 27.3kW BOQ E38,000 → 69p → E10,000 + 69×E333 ≈ E38,000 ✓
        //   Cross-check: 110kW BOQ E95,681  → 275p → E10,000 + 275×E294 ≈ E96,000 ✓
        const mountingCostFactors = { 'metal': 1.0, 'tile': 1.2, 'concrete': 1.1, 'ground': 1.3 };
        const mountingBase = 10000;
        const mountingPerPanel = 295;
        const mountingCost = (mountingBase + actualPanels * mountingPerPanel) * (mountingCostFactors[roofType] || 1);

        // 5. Electrical & Trunking (P9000/P2000 trunking, comm cable, CTs, smart meter)
        //    BOQ: 52,468 ÷ 264 panels = R199/panel (scales with panel count)
        //    Electrical room (isolator, SWA cable, BCW earthing, accessories): E20,460 FIXED
        //    (isolator cabinet, SWA cable, BCW earthing are fixed regardless of system size)
        const electricalPerPanelCost = 1108;
        const electricalRoomFixed = 20460;
        const electricalCost = (actualPanels * electricalPerPanelCost) + electricalRoomFixed;

        // 6. Labour — derived directly from BOQ totals
        //    BOQ tasks (264 panels, 110kW):
        //      Roof mounting structure:        4 days × E2,800 = E11,200
        //      Solar panel mounting:           7 days × E2,800 = E19,600
        //      Wiring panels:                  2 days × E2,800 =  E5,600
        //      Trunking and conduiting:        4 days × E2,200 =  E8,800
        //      Inverter & electrical wiring:   2 days × E2,800 =  E5,600
        //      Testing, commissioning & COC:   1 day  × E3,600 =  E3,600
        //      BOQ subtotal: E54,400 → E54,400 ÷ 264 = E206/panel
        const labourCostPerPanel = 206;
        const laborCost = actualPanels * labourCostPerPanel;

        // 7. Transport
        //    BOQ total: E8,500 flat — fixed delivery cost regardless of system size.
        const transportCost = 8500;
        const miscCost = transportCost;

        // Subtotal (before VAT) — kept as raw float to avoid double-rounding when VAT is applied
        const rawSubtotal = panelCost + inverterCost + batteryCost + mountingCost +
            electricalCost + laborCost + miscCost;

        // 8. VAT (15% on subtotal) — round once at the total level, not intermediate steps
        const subtotal = Math.round(rawSubtotal);
        const vat = Math.round(rawSubtotal * 0.15);

        // 9. Maintenance Plan
        let maintenanceCost = 0;
        switch (maintenancePlan) {
            case 'basic':
                maintenanceCost = 1500 + (actualPanels * 10); // More panels = slightly higher maintenance
                break;
            case 'premium':
                maintenanceCost = 3000 + (actualPanels * 15);
                break;
            case 'extended':
                maintenanceCost = 5000 + (actualPanels * 20);
                break;
            default:
                maintenanceCost = 1500 + (actualPanels * 10);
                break;
        }

        // Final Total (subtotal + VAT) — round once from raw to match "how it is calculated" panel
        const totalCost = Math.round(rawSubtotal * 1.15);

        // Performance Calculations
        const dailyProduction = actualSystemSizeKW * sunHours * 0.85; // 85% efficiency
        const monthlyProduction = dailyProduction * 30;
        const coveragePercentage = monthlyUnits > 0 ? (monthlyProduction / monthlyUnits) * 100 : 0;

        // Savings Calculations
        const monthlySavings = Math.min(monthlyProduction * 2.5, monthlyBill * 0.95); // Max 95% of bill
        const annualSavings = monthlySavings * 12;
        const paybackYears = annualSavings > 0 ? totalCost / annualSavings : 0;

        // ROI Calculation (25-year lifespan)
        const lifetimeSavings = annualSavings * 25;
        const roiPercentage = totalCost > 0 ? ((lifetimeSavings - totalCost) / totalCost) * 100 : 0;

        return {
            // System Specifications
            systemSizeKW: actualSystemSizeKW.toFixed(1),
            panelCount: actualPanels,
            requiredSystemSizeKW: requiredAdjustedSystemSizeKW.toFixed(1),
            maxPossibleSystemSizeKW: maxSystemSizeByRoof.toFixed(1),
            inverterSize: actualSystemSizeKW.toFixed(1),
            inverterType,
            batterySize: actualBatterySize,
            dailyProduction: dailyProduction.toFixed(1),
            monthlyProduction: monthlyProduction.toFixed(0),
            coveragePercentage: coveragePercentage.toFixed(0),
            isRoofLimited,
            isEnergyLimited,
            extraPanelsPossible: Math.max(0, extraPanelsPossible),
            extraRoofSpaceAvailable: Math.max(0, extraRoofSpaceAvailable),
            usedRoofSpace: usedRoofSpace.toFixed(0),
            roofUsagePercentage: roofUsagePercentage.toFixed(0),

            // Cost Breakdown
            costBreakdown: {
                panels: Math.round(panelCost),
                inverter: Math.round(inverterCost),
                batteries: Math.round(batteryCost),
                mounting: Math.round(mountingCost),
                electrical: Math.round(electricalCost),
                labor: Math.round(laborCost),
                misc: Math.round(miscCost),
                subtotal: Math.round(subtotal),
                vat: Math.round(vat),
                maintenance: Math.round(maintenanceCost)
            },

            // Totals
            equipmentTotal: Math.round(panelCost + inverterCost + batteryCost + mountingCost + electricalCost),
            installationTotal: Math.round(laborCost + miscCost),
            subtotal: Math.round(subtotal),
            totalCost: Math.round(totalCost),

            // Financial Metrics
            monthlySavings: Math.round(monthlySavings),
            annualSavings: Math.round(annualSavings),
            paybackYears: paybackYears.toFixed(1),
            roiPercentage: roiPercentage.toFixed(0),
        };
    }, [
        monthlyBill,
        monthlyUnits,
        roofSize,
        roofType,
        systemType,
        batteryBackup,
        backupHours,
        maintenancePlan,
        desiredSystemSizeKW
    ]);

    useEffect(() => {
        if (onDataUpdate) {
            const solarData = calculateSolarSystem();
            onDataUpdate({
                monthlyBill,
                monthlyUnits,
                roofSize,
                roofType,
                roofPitch,
                systemType,
                batteryBackup,
                backupHours,
                maintenancePlan,
                calculatedResults: solarData
            });
        }
    }, [
        calculateSolarSystem,
        onDataUpdate,
        monthlyBill,
        monthlyUnits,
        roofSize,
        roofType,
        roofPitch,
        systemType,
        batteryBackup,
        backupHours,
        maintenancePlan
    ]);

    // Function to handle roof size input change
    const handleRoofSizeChange = (e) => {
        const raw = e.target.value;
        setRoofSizeInput(raw);
        const value = parseInt(raw, 10);
        if (!isNaN(value) && value >= 0) {
            setRoofSize(value);
        }
    };

    // Roof/ground size limits based on property type
    const roofSliderMax = propertyType === 'residential' ? 500 : propertyType === 'commercial' ? 1000 : 5000;
    const roofSliderStep = propertyType === 'industrial' ? 50 : 10;
    const isIndustrial = propertyType === 'industrial';
    const groundMax = propertyType === 'residential' ? 500 : propertyType === 'commercial' ? 1000 : null;

    // Check if ground size is valid
    const isGroundSizeValid = roofType === 'ground'
        ? roofSize >= 20 && (isIndustrial || roofSize <= (groundMax || Infinity))
        : true;

    useEffect(() => {
        if (setIsSolarDataValid) {
            setIsSolarDataValid(isGroundSizeValid);
        }
    }, [isGroundSizeValid, setIsSolarDataValid]);

    const solarData = calculateSolarSystem();

    // Intermediate values for the live calculation detail display
    // NOTE: No intermediate .toFixed() rounding — raw floats used throughout to exactly
    // mirror calculateSolarSystem() and produce an identical total.
    const calcCostPerKwh = 2.5;
    const calcDailyUsage = monthlyUnits / 30;
    const calcRequiredKW = calcDailyUsage / sunHours;
    const calcAdjustedKW = calcRequiredKW * 1.15;
    const calcMaxPanels = Math.floor(roofSize / 2);
    const calcDcAcRatioForMax = systemType === 'grid-tie' ? 1.5 : 1.0;
    const calcMaxSystemKW = (calcMaxPanels * 600) / calcDcAcRatioForMax / 1000;
    const calcAutoKW = Math.min(calcAdjustedKW, calcMaxSystemKW);
    // Do NOT clamp here — allow desired size to exceed roof max so the warning banner shows
    const calcActualKW = desiredSystemSizeKW ?? calcAutoKW;
    const calcDcAcRatio = systemType === 'grid-tie' ? 1.5 : 1.0;
    const calcActualPanels = Math.ceil((calcActualKW * 1000 * calcDcAcRatio) / 600);
    // BOQ-derived rates (Segen SA + 35% markup)
    const calcInverterCostPerKW = systemType === 'grid-tie' ? 982 : systemType === 'hybrid' ? 1800 : 2000;
    const calcRawPanelCost = calcActualPanels * 2300;
    const calcPanelCost = Math.round(calcRawPanelCost);
    const calcRawInverterCost = calcActualKW * calcInverterCostPerKW;
    const calcInverterCost = Math.round(calcRawInverterCost);
    const calcBatteryDailyKwh = desiredSystemSizeKW
        ? calcActualKW * sunHours * 0.85  // system output when size is manually set
        : calcDailyUsage;                 // bill-derived consumption in auto mode
    const calcBackupPower = (calcBatteryDailyKwh / 24) * backupHours;
    const calcBatteryKwh = batteryBackup === 'yes' && systemType !== 'grid-tie' ? Math.ceil((calcBackupPower * 1.2) / 0.8) : 0;
    // Battery units: 5kWh @ E5,000 each
    const calcBatteryUnitCount = calcBatteryKwh > 0 ? Math.ceil(calcBatteryKwh / 5) : 0;
    const calcRawBatteryCost = calcBatteryUnitCount * 5000;
    const calcBatteryCost = Math.round(calcRawBatteryCost);
    const calcMountingFactor = ({ metal: 1.0, tile: 1.2, concrete: 1.1, ground: 1.3 })[roofType] || 1;
    // Raw (unrounded) versions — mirrors calculateSolarSystem() exactly
    const calcRawMountingCost = (10000 + calcActualPanels * 295) * calcMountingFactor;
    const calcRawElectricalCost = (calcActualPanels * 1108) + 20460;
    const calcRawLaborCost = calcActualPanels * 206;
    const calcRawTransportCost = 8500; // flat BOQ total, independent of panel count
    // Rounded display versions
    const calcMountingCost = Math.round(calcRawMountingCost);
    const calcElectricalCost = Math.round(calcRawElectricalCost);
    const calcLaborCost = Math.round(calcRawLaborCost);
    const calcTransportCost = Math.round(calcRawTransportCost);
    const calcRawSubtotal = calcRawPanelCost + calcRawInverterCost + calcRawBatteryCost + calcRawMountingCost + calcRawElectricalCost + calcRawLaborCost + calcRawTransportCost;
    // Rounded subtotal and VAT — for display only
    const calcSubtotal = Math.round(calcRawSubtotal);
    const calcVat = Math.round(calcRawSubtotal * 0.15);
    const calcTotal = Math.round(calcRawSubtotal * 1.15);
    const calcDailyProduction = +(calcActualKW * sunHours * 0.85).toFixed(2);
    const calcMonthlyProduction = Math.round(calcDailyProduction * 30);
    const calcCoverage = monthlyUnits > 0 ? Math.round((calcMonthlyProduction / monthlyUnits) * 100) : 0;
    const calcMonthlySavings = Math.round(Math.min(calcMonthlyProduction * 2.5, monthlyBill * 0.95));
    const calcAnnualSavings = calcMonthlySavings * 12;
    const calcPayback = calcAnnualSavings > 0 ? +(calcTotal / calcAnnualSavings).toFixed(1) : 0;
    const calcLifetimeSavings = calcAnnualSavings * 25;
    const calcROI = calcTotal > 0 ? Math.round(((calcLifetimeSavings - calcTotal) / calcTotal) * 100) : 0;

    return (
        <div className="bg-white rounded-xl shadow-lg p-8" data-aos="fade-up">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div className="flex items-center">
                    <div className="p-3 bg-green-100 rounded-lg mr-4 shrink-0">
                        <MdSolarPower className="text-3xl text-green-600" />
                    </div>
                    <div>
                        <h2 className="text-4xl sm:text-5xl font-bold text-gray-800">Solar Energy Estimator</h2>
                        <p className="text-gray-600 text-sm sm:text-base">Professional solar system design based on your energy needs</p>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => setShowCalcDetails(!showCalcDetails)}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg text-blue-700 font-medium text-sm transition-all w-full sm:w-auto shrink-0"
                >
                    How is this calculated?
                    <span className={`inline-block transform transition-transform duration-200 ${showCalcDetails ? 'rotate-180' : ''}`}>&#9660;</span>
                </button>
            </div>

            {/* Calculation Details Panel */}
            {showCalcDetails && (
                <div className="mb-8 border border-blue-200 rounded-xl overflow-hidden">
                    <div className="bg-blue-600 px-6 py-4">
                        <h3 className="text-xl font-bold text-white">Step-by-Step Calculation Breakdown</h3>
                        <p className="text-blue-100 text-sm mt-1">All values update live as you adjust inputs above</p>
                    </div>

                    <div className="divide-y divide-gray-100">

                        {/* Step 1: Energy Demand */}
                        <div className="p-6 bg-white">
                            <h4 className="font-bold text-gray-800 text-base mb-4 flex items-center gap-2">
                                <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">1</span>
                                Energy Demand (inferred from bill)
                            </h4>
                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Monthly kWh</div>
                                    <div className="bg-gray-50 rounded-lg p-3 font-mono text-sm">monthlyUnits = monthlyBill &divide; costPerKWh</div>
                                    <div className="bg-blue-50 rounded-lg p-3 text-sm border border-blue-100">
                                        E{fmtNum(monthlyBill)} &divide; E{calcCostPerKwh}/kWh
                                        <span className="block mt-1 font-bold text-blue-700">= {monthlyUnits} kWh/month</span>
                                    </div>
                                    <p className="text-xs text-gray-500 italic">E2.50/kWh is the average Eswatini electricity tariff — dividing the bill by the rate gives the units consumed</p>
                                </div>
                                <div className="space-y-2">
                                    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Daily kWh</div>
                                    <div className="bg-gray-50 rounded-lg p-3 font-mono text-sm">dailyUsage = monthlyUnits &divide; 30 days</div>
                                    <div className="bg-blue-50 rounded-lg p-3 text-sm border border-blue-100">
                                        {monthlyUnits} kWh &divide; 30 days
                                        <span className="block mt-1 font-bold text-blue-700">= {calcDailyUsage.toFixed(2)} kWh/day</span>
                                    </div>
                                    <p className="text-xs text-gray-500 italic">Dividing by 30 converts monthly consumption into a daily average that the solar system must meet each day</p>
                                </div>
                            </div>
                        </div>

                        {/* Step 2: Possible System Size */}
                        <div className="p-6 bg-gray-50">
                            <h4 className="font-bold text-gray-800 text-base mb-4 flex items-center gap-2">
                                <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">2</span>
                                Possible System Size
                            </h4>
                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Base Size</div>
                                    <div className="bg-white rounded-lg p-3 font-mono text-sm border">requiredKW = dailyUsage &divide; sunHours</div>
                                    <div className="bg-green-50 rounded-lg p-3 text-sm border border-green-100">
                                        {calcDailyUsage.toFixed(2)} kWh &divide; {sunHours}h
                                        <span className="block mt-1 font-bold text-green-700">= {calcRequiredKW.toFixed(2)} kW</span>
                                    </div>
                                    <p className="text-xs text-gray-500 italic">We use <strong>{sunHours} peak sun hours</strong> — Eswatini's annual average. This accounts for seasonal variation across summer and winter and gives a reliable year-round system size estimate.</p>
                                </div>
                                <div className="space-y-2">
                                    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">+ 15% System Losses</div>
                                    <div className="bg-white rounded-lg p-3 font-mono text-sm border">adjustedKW = requiredKW &times; 1.15</div>
                                    <div className="bg-green-50 rounded-lg p-3 text-sm border border-green-100">
                                        {calcRequiredKW.toFixed(2)} kW &times; 1.15
                                        <span className="block mt-1 font-bold text-green-700">= {calcAdjustedKW.toFixed(2)} kW needed</span>
                                    </div>
                                    <p className="text-xs text-gray-500 italic">Covers inverter loss, wiring resistance, dust &amp; temperature derating</p>
                                </div>
                            </div>
                        </div>

                        {/* Step 3: Roof / Ground Capacity */}
                        <div className="p-6 bg-white">
                            <h4 className="font-bold text-gray-800 text-base mb-4 flex items-center gap-2">
                                <span className="bg-orange-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">3</span>
                                {roofType === 'ground' ? 'Ground' : 'Roof'} Capacity
                            </h4>
                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Max Panels by Space</div>
                                    <div className="bg-gray-50 rounded-lg p-3 font-mono text-sm">maxPanels = space &divide; 2m&sup2; per panel</div>
                                    <div className="bg-orange-50 rounded-lg p-3 text-sm border border-orange-100">
                                        {roofSize}m&sup2; &divide; 2m&sup2;
                                        <span className="block mt-1 font-bold text-orange-700">= {calcMaxPanels} panels maximum</span>
                                    </div>
                                    <p className="text-xs text-gray-500 italic">A standard 600W panel measures roughly 2m² (1.7m &times; 1.2m). Dividing available space by 2 gives the maximum number of panels that will fit</p>
                                </div>
                                <div className="space-y-2">
                                    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Max System by Space</div>
                                    <div className="bg-gray-50 rounded-lg p-3 font-mono text-sm">maxKW = maxPanels &times; 600W &divide; 1000</div>
                                    <div className="bg-orange-50 rounded-lg p-3 text-sm border border-orange-100">
                                        {calcMaxPanels} &times; 600W &divide; 1000
                                        <span className="block mt-1 font-bold text-orange-700">= {calcMaxSystemKW.toFixed(1)} kW maximum</span>
                                    </div>
                                    <p className="text-xs text-gray-500 italic">600W is the rated peak output of one standard solar panel. Multiplying by panel count gives total watts; dividing by 1,000 converts to kilowatts (1 kW = 1,000 W)</p>
                                </div>
                            </div>
                        </div>

                        {/* Step 4: Actual System */}
                        <div className="p-6 bg-gray-50">
                            <h4 className="font-bold text-gray-800 text-base mb-4 flex items-center gap-2">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">4</span>
                                Actual System Installed
                            </h4>
                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">System Size (need vs space)</div>
                                    {desiredSystemSizeKW ? (
                                        <>
                                            <div className="bg-white rounded-lg p-3 font-mono text-sm border">actualKW = min(desired, maxByRoof)</div>
                                            <div className="bg-purple-50 rounded-lg p-3 text-sm border border-purple-100">
                                                min({desiredSystemSizeKW} kW desired, {calcMaxSystemKW.toFixed(1)} kW max)
                                                <span className="block mt-1 font-bold text-purple-700">= {calcActualKW.toFixed(2)} kW installed</span>
                                            </div>
                                            <p className="text-xs text-gray-500 italic">
                                                Client-specified size — {(calcMaxSystemKW - calcActualKW).toFixed(1)} kW of available capacity unused
                                            </p>
                                        </>
                                    ) : (
                                        <>
                                            <div className="bg-white rounded-lg p-3 font-mono text-sm border">actualKW = min(adjusted, maxByRoof)</div>
                                            <div className="bg-purple-50 rounded-lg p-3 text-sm border border-purple-100">
                                                min({calcAdjustedKW.toFixed(2)} kW, {calcMaxSystemKW.toFixed(1)} kW)
                                                <span className="block mt-1 font-bold text-purple-700">= {calcActualKW.toFixed(2)} kW installed</span>
                                            </div>
                                            <p className="text-xs text-gray-500 italic">
                                                {calcAdjustedKW <= calcMaxSystemKW
                                                    ? `Energy-limited — roof has ${(calcMaxSystemKW - calcAdjustedKW).toFixed(1)} kW spare capacity`
                                                    : 'Roof-limited — not enough space for full required system'}
                                            </p>
                                        </>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Panel Count</div>
                                    <div className="bg-white rounded-lg p-3 font-mono text-sm border">
                                        {systemType === 'grid-tie'
                                            ? 'panels = ⌈actualKW × 1000 × 1.5 (DC:AC ratio) ÷ 600W⌉'
                                            : 'panels = ⌈actualKW × 1000 ÷ 600W⌉'}
                                    </div>
                                    <div className="bg-purple-50 rounded-lg p-3 text-sm border border-purple-100">
                                        {systemType === 'grid-tie' ? (
                                            <>
                                                &lceil;{+(calcActualKW * 1000).toFixed(0)}W &times; 1.5 &divide; 600W&rceil;
                                                <div className="text-xs text-gray-500 mt-1">= &lceil;{+(calcActualKW * 1000 * 1.5).toFixed(0)}W &divide; 600W&rceil;</div>
                                            </>
                                        ) : (
                                            <>&lceil;{+(calcActualKW * 1000).toFixed(0)}W &divide; 600W&rceil;</>
                                        )}
                                        <span className="block mt-1 font-bold text-purple-700">= {calcActualPanels} panels &times; 600W</span>
                                    </div>
                                    {systemType === 'grid-tie' ? (
                                        <p className="text-xs text-gray-500 italic">
                                            Grid-tie systems use a <strong>1.5 DC:AC ratio</strong> — the industry standard for grid-tied PV. The inverter is rated at the AC output (e.g. 110 kW), but you install 1.5× more DC panel capacity (e.g. 165 kW DC). The inverter clips at peak noon, but during mornings, evenings, and overcast hours the extra panels harvest significantly more energy than a 1:1 system would. &divide;600W then converts to panel count; &lceil;&rceil; rounds up to the next whole panel.
                                        </p>
                                    ) : (
                                        <p className="text-xs text-gray-500 italic">&times;1000 converts kW back to watts; &divide;600W gives how many panels are needed. The ceiling (&lceil;&rceil;) rounds up — you can't install half a panel, so we always go to the next whole number</p>
                                    )}
                                    <p className="text-xs text-gray-500 italic">
                                        Uses {calcActualPanels * 2}m&sup2; of {roofSize}m&sup2; ({roofSize > 0 ? Math.round((calcActualPanels * 2 / roofSize) * 100) : 0}% of available space)
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Step 5: Battery Sizing */}
                        <div className="p-6 bg-white">
                            <h4 className="font-bold text-gray-800 text-base mb-4 flex items-center gap-2">
                                <span className="bg-teal-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">5</span>
                                Battery Sizing
                                {calcBatteryKwh === 0 && (
                                    <span className="ml-2 text-xs font-normal text-gray-400 italic">
                                        ({systemType === 'grid-tie' ? 'not applicable — grid-tie systems do not use batteries' : 'no battery backup selected'})
                                    </span>
                                )}
                            </h4>
                            {calcBatteryKwh > 0 ? (
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Step 1 — Raw Backup Power Needed</div>
                                        {desiredSystemSizeKW ? (
                                            <>
                                                <div className="bg-white rounded-lg p-3 font-mono text-sm border">backupPower = (systemOutput ÷ 24) × backupHours</div>
                                                <div className="bg-teal-50 rounded-lg p-3 text-sm border border-teal-100">
                                                    systemOutput = {calcActualKW.toFixed(2)} kW × {sunHours}h × 0.85 = {calcBatteryDailyKwh.toFixed(2)} kWh/day
                                                    <div className="text-xs text-gray-500 mt-1">({calcBatteryDailyKwh.toFixed(2)} ÷ 24) × {backupHours}h</div>
                                                    <span className="block mt-1 font-bold text-teal-700">= {calcBackupPower.toFixed(2)} kWh raw</span>
                                                </div>
                                                <p className="text-xs text-gray-500 italic">
                                                    Because you've set a manual system size, the battery is sized to what your <strong>{calcActualKW.toFixed(1)} kW system actually produces</strong> ({calcBatteryDailyKwh.toFixed(1)} kWh/day), not your bill consumption. Dividing by 24 gives the average hourly output. Multiplying by {backupHours}h gives the raw energy the battery must store for a {backupHours}-hour outage.
                                                </p>
                                            </>
                                        ) : (
                                            <>
                                                <div className="bg-white rounded-lg p-3 font-mono text-sm border">backupPower = (dailyUsage ÷ 24) × backupHours</div>
                                                <div className="bg-teal-50 rounded-lg p-3 text-sm border border-teal-100">
                                                    ({calcDailyUsage.toFixed(2)} kWh ÷ 24) × {backupHours}h
                                                    <span className="block mt-1 font-bold text-teal-700">= {calcBackupPower.toFixed(2)} kWh raw</span>
                                                </div>
                                                <p className="text-xs text-gray-500 italic">
                                                    Dividing daily usage by 24 gives the average power draw per hour. Multiplying by your {backupHours}-hour backup requirement gives the raw energy the battery must store to keep your home running through an outage of that length.
                                                </p>
                                            </>
                                        )}
                                    </div>
                                    <div className="space-y-2">
                                        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Step 2 — Apply Safety Buffer &amp; Depth-of-Discharge</div>
                                        <div className="bg-white rounded-lg p-3 font-mono text-sm border">batteryKwh = ⌈(backupPower × 1.2) ÷ 0.8⌉</div>
                                        <div className="bg-teal-50 rounded-lg p-3 text-sm border border-teal-100">
                                            ⌈({calcBackupPower.toFixed(2)} × 1.2) ÷ 0.8⌉
                                            <div className="text-xs text-gray-500 mt-1">= ⌈{(calcBackupPower * 1.2).toFixed(2)} ÷ 0.8⌉ = ⌈{((calcBackupPower * 1.2) / 0.8).toFixed(2)}⌉</div>
                                            <span className="block mt-1 font-bold text-teal-700">= {calcBatteryKwh} kWh → {calcBatteryUnitCount} × 5kWh battery units needed</span>
                                        </div>
                                        <p className="text-xs text-gray-500 italic">
                                            <strong>×1.2</strong> adds a 20% safety buffer — batteries degrade over time and real loads spike above the average, so we oversize slightly to keep performance reliable.
                                        </p>
                                        <p className="text-xs text-gray-500 italic">
                                            <strong>÷0.8</strong> accounts for depth-of-discharge (DoD) — LiFePO4 batteries should only be discharged to 20% remaining to protect cycle life. So only 80% of the nameplate capacity is safely usable. Dividing by 0.8 gives the total capacity needed so that 80% of it equals the energy required.
                                        </p>
                                        <p className="text-xs text-gray-500 italic">
                                            <strong>⌈ ⌉</strong> rounds up to the next whole kWh — battery banks are sized in whole units, so we never round down and leave the system short.
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-500 italic">
                                    {systemType === 'grid-tie'
                                        ? 'Grid-tie systems export surplus power back to the grid instead of storing it, so no battery is included. Switch to Hybrid or Off-Grid to see battery sizing.'
                                        : 'Battery backup is set to "No". Enable battery backup above to see how the required battery capacity is calculated.'}
                                </div>
                            )}
                        </div>

                        {/* Step 6: Cost Breakdown */}
                        <div className="p-6 bg-gray-50">
                            <h4 className="font-bold text-gray-800 text-base mb-4 flex items-center gap-2">
                                <span className="bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">6</span>
                                Cost Breakdown
                            </h4>
                            <div className="grid md:grid-cols-2 gap-3 mb-4">
                                <div className="space-y-3">
                                    <div className="bg-gray-50 rounded-lg p-3">
                                        <div className="text-xs text-gray-500 font-mono mb-1">panels = panelCount × E2 300/panel</div>
                                        <div className="text-sm">{calcActualPanels} panels × E2 300 = <span className="font-bold">E{fmtNum(calcPanelCost)}</span></div>
                                        <div className="text-xs text-gray-400 italic mt-1">625W Tier-1 mono panel (JA/Jinko/Canadian Solar) at E2 300/panel. Panel count already reflects DC oversizing (grid-tie: ×1.5), so multiplying by the unit price gives the true panel spend.</div>
                                    </div>
                                    <div className="bg-gray-50 rounded-lg p-3">
                                        <div className="text-xs text-gray-500 font-mono mb-1">inverter = systemKW &times; E{fmtNum(calcInverterCostPerKW)}/kW</div>
                                        <div className="text-sm">{calcActualKW.toFixed(2)} kW &times; E{fmtNum(calcInverterCostPerKW)} = <span className="font-bold">E{fmtNum(calcInverterCost)}</span></div>
                                        <div className="text-xs text-gray-400 italic mt-1">
                                            Rate depends on system type — <strong>Grid-tie: E982/kW</strong> (Solis S6, E108,000 ÷ 110kW) · <strong>Hybrid: E1 800/kW</strong> · <strong>Off-grid: E2 000/kW</strong>. Hybrid and off-grid inverters cost more because they include battery management circuitry.
                                        </div>
                                    </div>
                                    {calcBatteryKwh > 0 ? (
                                        <div className="bg-gray-50 rounded-lg p-3">
                                            <div className="text-xs text-gray-500 font-mono mb-1">batteries = ⌈batteryKwh ÷ 5⌉ units × E5 000/unit</div>
                                            <div className="text-xs text-gray-500 mb-1">Energy needed = {calcBatteryKwh} kWh → ⌈{calcBatteryKwh} ÷ 5⌉ = {calcBatteryUnitCount} units</div>
                                            <div className="text-sm">{calcBatteryUnitCount} units × E5 000 = <span className="font-bold">E{fmtNum(calcBatteryCost)}</span></div>
                                            <div className="text-xs text-gray-400 italic mt-1">Each battery unit is 5 kWh @ E5 000 installed. The client can add more units at any time to increase backup capacity — the system is designed to scale.</div>
                                        </div>
                                    ) : (
                                        <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-400 italic">No batteries — E0 (see Step 5)</div>
                                    )}
                                    <div className="bg-gray-50 rounded-lg p-3">
                                        <div className="text-xs text-gray-500 font-mono mb-1">mounting = (E10 000 base + panels × E295) × roofFactor({calcMountingFactor})</div>
                                        <div className="text-sm">(E10 000 + {calcActualPanels} × E295) × {calcMountingFactor} = <span className="font-bold">E{fmtNum(calcMountingCost)}</span></div>
                                        <div className="text-xs text-gray-400 italic mt-1">
                                            E10 000 covers fixed setup costs (scaffolding, equipment, site preparation) that apply regardless of system size. E295/panel covers the structural hardware per panel (IBR rails, end/middle clamps, tek screws).
                                        </div>
                                        <div className="text-xs text-gray-400 italic mt-1">
                                            <strong>roofFactor</strong> reflects installation difficulty by surface type — Metal: 1.0 (easiest) · Concrete: 1.1 · Tile: 1.2 · Ground mount: 1.3 (needs ground anchors &amp; frames). Your <strong>{roofType}</strong> roof = {calcMountingFactor}.
                                        </div>
                                    </div>
                                </div>
                                <div className="space-y-3">
                                    <div className="bg-gray-50 rounded-lg p-3">
                                        <div className="text-xs text-gray-500 font-mono mb-1">electrical = panels × E1 108/panel + E20 460 (fixed room cost)</div>
                                        <div className="text-sm">({calcActualPanels} × E1 108) + E20 460 = <span className="font-bold">E{fmtNum(calcElectricalCost)}</span></div>
                                        <div className="text-xs text-gray-400 italic mt-1">E1 108/panel covers DC hardware (combiner box, MC4 connectors, PV cables: E909/panel) plus trunking, CTs and smart meter (E199/panel) — items that scale with panel count. E20 460 is a <strong>fixed lump sum</strong> for the electrical room (isolator cabinet, SWA cable, BCW earthing wire, and cable accessories) — these are one-per-installation items that don’t grow with system size.</div>
                                    </div>
                                    <div className="bg-gray-50 rounded-lg p-3">
                                        <div className="text-xs text-gray-500 font-mono mb-1">labor = panels × E206/panel</div>
                                        <div className="text-xs text-gray-500 mb-2">

                                        </div>
                                        <div className="text-sm">{calcActualPanels} × E206 = <span className="font-bold">E{fmtNum(calcLaborCost)}</span></div>
                                        <div className="text-xs text-gray-400 italic mt-1">
                                            Labor breakdown for 264 panels: roof mounting structure (4d × E2 800), panel mounting (7d × E2 800), wiring (2d × E2 800), trunking (4d × E2 200), inverter wiring (2d × E2 800), commissioning &amp; COC (1d × E3 600) = E54 400 ÷ 264 panels = <strong>E206/panel</strong>. Installation rate. Mounting, electrical &amp; civil work are costed separately above.
                                        </div>
                                        <div className="text-xs text-gray-400 italic mt-1">

                                        </div>
                                    </div>
                                    <div className="bg-gray-50 rounded-lg p-3">
                                        <div className="text-xs text-gray-500 font-mono mb-1">transport = E8 500 (flat total)</div>
                                        <div className="text-sm">E8 500 fixed = <span className="font-bold">E{fmtNum(calcTransportCost)}</span></div>
                                        <div className="text-xs text-gray-400 italic mt-1">Fixed delivery cost of E8 500 — covers transport of all equipment to site regardless of system size.</div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-red-50 rounded-xl p-4 border border-red-100 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                                <div>
                                    <div className="text-gray-500 mb-1">Subtotal</div>
                                    <div className="font-bold text-gray-800">E{fmtNum(calcSubtotal)}</div>
                                </div>
                                <div>
                                    <div className="text-gray-500 mb-1">+ VAT (15%)</div>
                                    <div className="font-bold text-orange-600">E{fmtNum(calcVat)}</div>
                                </div>
                                <div>
                                    <div className="text-gray-500 mb-1">Total Investment (incl. VAT)</div>
                                    <div className="font-bold text-red-700 text-base">E{fmtNum(calcTotal)}</div>
                                </div>
                            </div>
                        </div>

                        {/* Step 7: System Performance */}
                        <div className="p-6 bg-gray-50">
                            <h4 className="font-bold text-gray-800 text-base mb-4 flex items-center gap-2">
                                <span className="bg-yellow-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">7</span>
                                System Performance
                            </h4>
                            <div className="grid md:grid-cols-3 gap-4">
                                <div className="space-y-2">
                                    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Daily Production</div>
                                    <div className="bg-white rounded-lg p-3 font-mono text-sm border">dailyProd = kW &times; sunHours &times; 0.85</div>
                                    <div className="bg-yellow-50 rounded-lg p-3 text-sm border border-yellow-100">
                                        {calcActualKW.toFixed(2)} kW &times; {sunHours}h &times; 0.85
                                        <span className="block mt-1 font-bold text-yellow-700">= {calcDailyProduction} kWh/day</span>
                                    </div>
                                    <p className="text-xs text-gray-500 italic">Uses Eswatini's fixed average of {sunHours} peak sun hours/day. &times;0.85 applies an 85% performance ratio accounting for real-world losses (heat, dust, wiring resistance, inverter inefficiency)</p>
                                </div>
                                <div className="space-y-2">
                                    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Monthly Production</div>
                                    <div className="bg-white rounded-lg p-3 font-mono text-sm border">monthlyProd = dailyProd &times; 30</div>
                                    <div className="bg-yellow-50 rounded-lg p-3 text-sm border border-yellow-100">
                                        {calcDailyProduction} kWh &times; 30
                                        <span className="block mt-1 font-bold text-yellow-700">= {calcMonthlyProduction} kWh/month</span>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Energy Coverage</div>
                                    <div className="bg-white rounded-lg p-3 font-mono text-sm border">coverage = production &divide; usage &times; 100</div>
                                    <div className="bg-yellow-50 rounded-lg p-3 text-sm border border-yellow-100">
                                        {calcMonthlyProduction} &divide; {monthlyUnits} &times; 100
                                        <span className="block mt-1 font-bold text-yellow-700">= {calcCoverage}% covered</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Step 8: Financial Returns */}
                        <div className="p-6 bg-white">
                            <h4 className="font-bold text-gray-800 text-base mb-4 flex items-center gap-2">
                                <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">8</span>
                                Financial Returns
                            </h4>
                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-3">
                                    <div className="space-y-2">
                                        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Monthly Savings</div>
                                        <div className="bg-gray-50 rounded-lg p-3 font-mono text-sm">savings = min(monthlyProduction &times; E2.50, monthlyBill &times; 95%)</div>
                                        <div className="bg-emerald-50 rounded-lg p-3 text-sm border border-emerald-100">
                                            min({calcMonthlyProduction} kWh &times; E2.50, E{fmtNum(monthlyBill)} &times; 95%)
                                            <div className="text-xs text-gray-500 mt-1">= min(E{Math.round(calcMonthlyProduction * fmtNum(2.5))}, E{Math.round(monthlyBill * fmtNum(0.95))})</div>
                                            <span className="block mt-1 font-bold text-emerald-700">= E{fmtNum(calcMonthlySavings)}/month</span>
                                        </div>
                                        <p className="text-xs text-gray-500 italic">&times;E2.50/kWh converts solar production into monetary savings at the current electricity tariff. The min() cap at 95% of the bill prevents over-promising — there are always small standing charges and night-time loads</p>
                                    </div>
                                    <div className="space-y-2">
                                        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Annual Savings</div>
                                        <div className="bg-gray-50 rounded-lg p-3 font-mono text-sm">annualSavings = monthlySavings &times; 12</div>
                                        <div className="bg-emerald-50 rounded-lg p-3 text-sm border border-emerald-100">
                                            E{fmtNum(calcMonthlySavings)} &times; 12
                                            <span className="block mt-1 font-bold text-emerald-700">= E{fmtNum(calcAnnualSavings)}/year</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="space-y-3">
                                    <div className="space-y-2">
                                        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Payback Period</div>
                                        <div className="bg-gray-50 rounded-lg p-3 font-mono text-sm">payback = totalCost &divide; annualSavings</div>
                                        <div className="bg-emerald-50 rounded-lg p-3 text-sm border border-emerald-100">
                                            E{fmtNum(calcTotal)} &divide; E{fmtNum(calcAnnualSavings)}
                                            <span className="block mt-1 font-bold text-emerald-700">= {calcPayback} years</span>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">25-Year ROI</div>
                                        <div className="bg-gray-50 rounded-lg p-3 font-mono text-sm">ROI = (annualSavings &times; 25 &minus; total cost) &divide; total cost &times; 100</div>
                                        <div className="bg-emerald-50 rounded-lg p-3 text-sm border border-emerald-100">
                                            (E{fmtNum(calcLifetimeSavings)} &minus; E{fmtNum(calcTotal)}) &divide; E{fmtNum(calcTotal)} &times; 100
                                            <span className="block mt-1 font-bold text-emerald-700">= {calcROI}% ROI over 25 years</span>
                                        </div>
                                        <p className="text-xs text-gray-500 italic">&times;25 is the standard solar panel warranty lifetime. Subtracting the initial investment from lifetime savings gives net profit; dividing by cost gives return as a percentage of money invested</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column - Energy Inputs */}
                <div className="space-y-6">
                    {/* Monthly Bill Input */}
                    <div className={systemSizeMode === 'manual' ? 'opacity-40 pointer-events-none select-none' : ''}>
                        <label className="block text-gray-700 font-medium mb-4">
                            <div className="flex items-center mb-2">
                                <FiDollarSign className="mr-2" />
                                Monthly Electricity Bill
                                {systemSizeMode === 'manual' && (
                                    <span className="ml-2 text-xs font-normal text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">disabled in manual mode</span>
                                )}
                            </div>
                            <div className="text-3xl font-bold text-green-600 mb-2">E{fmtNum(monthlyBill)}</div>
                        </label>
                        <input
                            type="range"
                            min="100"
                            max="500000"
                            step="12.5"
                            value={monthlyBill}
                            disabled={systemSizeMode === 'manual'}
                            onChange={(e) => setMonthlyBill(parseInt(e.target.value))}
                            className="w-full h-3 bg-gradient-to-r from-green-400 to-green-600 rounded-lg appearance-none cursor-pointer disabled:cursor-not-allowed [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-4 [&::-webkit-slider-thumb]:border-green-500 [&::-webkit-slider-thumb]:shadow-lg"
                        />
                        <div className="flex justify-between text-sm text-gray-500 mt-2">
                            <span>E100</span>
                            <span>E500 000</span>
                        </div>
                        <p className="text-sm text-green-600 mt-2">
                            ≈ {monthlyUnits} kWh per month
                        </p>
                    </div>

                    {/* Property Type Selector */}
                    <div>
                        <label className="block text-gray-700 font-medium mb-3">Property Type</label>
                        <div className="grid grid-cols-3 gap-3">
                            {[
                                { id: 'residential', label: 'Residential', icon: <FiHome className="text-xl" /> },
                                { id: 'commercial', label: 'Commercial', icon: <MdApartment className="text-xl" /> },
                                { id: 'industrial', label: 'Industrial', icon: <MdFactory className="text-xl" /> },
                            ].map(pt => (
                                <label key={pt.id} className="relative cursor-pointer">
                                    <input
                                        type="radio"
                                        name="propertyType"
                                        value={pt.id}
                                        checked={propertyType === pt.id}
                                        onChange={() => {
                                            setPropertyType(pt.id);
                                            // clamp roofSize to new max when switching down — preserve systemSizeMode
                                            const newMax = pt.id === 'residential' ? 500 : pt.id === 'commercial' ? 1000 : 5000;
                                            if (roofType !== 'ground' && roofSize > newMax) { setRoofSize(newMax); setRoofSizeInput(String(newMax)); }
                                            if (roofType === 'ground') {
                                                const gMax = pt.id === 'residential' ? 500 : pt.id === 'commercial' ? 1000 : null;
                                                if (gMax && roofSize > gMax) { setRoofSize(gMax); setRoofSizeInput(String(gMax)); }
                                            }
                                            // Do NOT reset systemSizeMode or desiredSystemSizeKW here
                                        }}
                                        className="sr-only"
                                    />
                                    <div className={`p-3 rounded-lg border-2 text-center transition-all ${propertyType === pt.id ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}>
                                        <div className="flex justify-center mb-1">{pt.icon}</div>
                                        <div className="text-xs font-semibold">{pt.label}</div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Roof/Ground Space Input */}
                    <div>
                        <label className="block text-gray-700 font-medium mb-4">
                            <div className="flex items-center mb-2">
                                <FiHome className="mr-2" />
                                {roofType === 'ground' ? 'Available Ground Space' : 'Available Roof Space'}
                            </div>
                            <div className="text-3xl font-bold text-green-600 mb-2">
                                {(roofType === 'ground' || isIndustrial) ? (
                                    <div className="flex flex-col">
                                        <div className="flex items-center mb-2">
                                            <input
                                                type="number"
                                                value={roofSizeInput}
                                                onChange={handleRoofSizeChange}
                                                onBlur={() => {
                                                    // On blur, clamp and normalise display
                                                    const val = parseInt(roofSizeInput, 10);
                                                    const safe = isNaN(val) || val < 0 ? 0 : val;
                                                    setRoofSize(safe);
                                                    setRoofSizeInput(String(safe));
                                                }}
                                                className={`w-full px-4 py-3 text-3xl font-bold rounded-lg focus:outline-none transition-all ${!isGroundSizeValid
                                                    ? 'border-2 border-red-500 text-red-600 bg-red-50'
                                                    : 'border-2 border-green-400 text-green-600 focus:border-green-600'
                                                    }`}
                                            />
                                            <span className={`ml-3 text-2xl ${!isGroundSizeValid ? 'text-red-600' : 'text-green-600'}`}>m²</span>
                                        </div>
                                        {!isGroundSizeValid && (
                                            <div className="text-sm text-red-600 bg-red-50 p-2 rounded-lg border border-red-200">
                                                ⚠️ Space must be at least 20m²{groundMax ? ` and no more than ${groundMax}m²` : ''} for this property type
                                            </div>
                                        )}
                                        {isGroundSizeValid && (
                                            <div className="text-sm text-gray-500">
                                                {isIndustrial ? 'No upper limit — enter any value above 20m²' : `Enter value between 20–${groundMax}m²`}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    `${roofSize}m²`
                                )}
                            </div>
                        </label>

                        {roofType !== 'ground' && !isIndustrial && (
                            <>
                                <input
                                    type="range"
                                    min="20"
                                    max={roofSliderMax}
                                    step={roofSliderStep}
                                    value={roofSize}
                                    onChange={(e) => { const v = parseInt(e.target.value); setRoofSize(v); setRoofSizeInput(String(v)); }}
                                    className="w-full h-3 bg-gradient-to-r from-green-400 to-green-600 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-4 [&::-webkit-slider-thumb]:border-green-500 [&::-webkit-slider-thumb]:shadow-lg"
                                />
                                <div className="flex justify-between text-sm text-gray-500 mt-2">
                                    <span>20m²</span>
                                    <span>{roofSliderMax}m²</span>
                                </div>
                            </>
                        )}

                        <p className="text-sm text-gray-500 mt-2">
                            Fits {Math.floor(roofSize / 2)} panels max ({roofSize}m² ÷ 2m² per panel)
                        </p>
                    </div>

                    {/* Roof Type */}
                    <div>
                        <label className="block text-gray-700 font-medium mb-3">Roof Type</label>
                        <div className="grid grid-cols-2 gap-3">
                            {[
                                { id: 'metal', label: 'Metal', icon: <MdRoofing className="text-2xl" /> },
                                { id: 'tile', label: 'Tile', icon: <MdGridOn className="text-2xl" /> },
                                { id: 'concrete', label: 'Concrete', icon: <MdOutlineSquareFoot className="text-2xl" /> },
                                { id: 'ground', label: 'Ground', icon: <MdLandscape className="text-2xl" /> }
                            ].map(type => (
                                <label key={type.id} className="relative">
                                    <input
                                        type="radio"
                                        name="roofType"
                                        value={type.id}
                                        checked={roofType === type.id}
                                        onChange={(e) => setRoofType(e.target.value)}
                                        className="sr-only peer"
                                    />
                                    <div className={`p-3 border-2 rounded-lg text-center cursor-pointer transition-all ${roofType === type.id
                                        ? 'border-green-500 bg-green-50'
                                        : 'border-gray-300 hover:border-green-300'
                                        }`}>
                                        <div className="flex justify-center mb-1">{type.icon}</div>
                                        <div className="text-sm font-medium text-gray-800">{type.label}</div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Roof Pitch — hidden for ground mount */}
                    {roofType !== 'ground' && (
                        <div>
                            <label className="block text-gray-700 font-medium mb-3">Roof Pitch</label>
                            <div className="space-y-2">
                                {[
                                    { id: 'flat', label: 'Flat', desc: '0–5°', icon: '▱' },
                                    { id: 'low', label: 'Low Pitch', desc: '5–15°', icon: '◿' },
                                    { id: 'medium', label: 'Medium Pitch', desc: '15–30°', icon: '◺' },
                                    { id: 'steep', label: 'Steep', desc: '30–45°', icon: '△' },
                                    { id: 'very_steep', label: 'Very Steep', desc: '45°+', icon: '▲' },
                                ].map(pitch => (
                                    <label key={pitch.id} className="relative">
                                        <input
                                            type="radio"
                                            name="roofPitch"
                                            value={pitch.id}
                                            checked={roofPitch === pitch.id}
                                            onChange={(e) => setRoofPitch(e.target.value)}
                                            className="sr-only peer"
                                        />
                                        <div className={`flex items-center justify-between px-4 py-2 border-2 rounded-lg cursor-pointer transition-all ${roofPitch === pitch.id
                                            ? 'border-green-500 bg-green-50'
                                            : 'border-gray-300 hover:border-green-300'
                                            }`}>
                                            <div className="flex items-center gap-3">
                                                <span className="text-xl">{pitch.icon}</span>
                                                <span className="text-sm font-medium text-gray-800">{pitch.label}</span>
                                            </div>
                                            <span className="text-xs text-gray-500">{pitch.desc}</span>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Middle Column - System Configuration */}
                <div className="space-y-6">
                    {/* System Type */}
                    <div>
                        <label className="block text-gray-700 font-medium mb-3">System Type</label>
                        <div className="space-y-3">
                            {[
                                { id: 'grid-tie', label: 'Grid-Tie', desc: 'No battery backup', icon: <MdElectricalServices className="text-2xl" />, color: 'gray' },
                                { id: 'hybrid', label: 'Hybrid', desc: 'Grid + Battery backup', icon: <FiZap className="text-2xl" />, color: 'blue' },
                                { id: 'off-grid', label: 'Off-Grid', desc: 'Complete independence', icon: <MdElectricMeter className="text-2xl" />, color: 'green' }
                            ].map(type => (
                                <label key={type.id} className="block">
                                    <div className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${systemType === type.id
                                        ? 'border-green-500 bg-green-50'
                                        : 'border-gray-300 hover:border-green-300'
                                        }`}>
                                        <input
                                            type="radio"
                                            name="systemType"
                                            value={type.id}
                                            checked={systemType === type.id}
                                            onChange={(e) => {
                                                setSystemType(e.target.value);
                                                if (e.target.value === 'grid-tie') {
                                                    setBatteryBackup('no');
                                                }
                                            }}
                                            className="h-5 w-5 text-green-600 mr-4"
                                        />
                                        <div className="flex-1">
                                            <div className="font-medium text-gray-800">{type.label}</div>
                                            <div className="text-sm text-gray-600">{type.desc}</div>
                                        </div>
                                        <div className={`${systemType === type.id ? 'text-green-600' : 'text-gray-400'}`}>{type.icon}</div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Battery Backup */}
                    {systemType !== 'grid-tie' && (
                        <div className="space-y-4">
                            <div>
                                <label className="block text-gray-700 font-medium mb-3">
                                    <div className="flex items-center mb-2">
                                        <FiBattery className="mr-2" />
                                        Battery Backup
                                    </div>
                                </label>
                                <div className="grid grid-cols-2 gap-4">
                                    {['no', 'yes'].map((option) => (
                                        <label key={option} className="relative">
                                            <input
                                                type="radio"
                                                name="batteryBackup"
                                                value={option}
                                                checked={batteryBackup === option}
                                                onChange={(e) => setBatteryBackup(e.target.value)}
                                                className="sr-only peer"
                                            />
                                            <div className={`p-4 border-2 rounded-lg text-center cursor-pointer transition-all ${batteryBackup === option
                                                ? 'border-green-500 bg-green-50 text-green-700 font-semibold'
                                                : 'border-gray-300 hover:border-green-300'
                                                }`}>
                                                <div className="text-lg capitalize">{option}</div>
                                                <div className="text-sm mt-1">
                                                    {option === 'yes' ? 'With backup power' : 'No batteries'}
                                                </div>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {batteryBackup === 'yes' && (
                                <>
                                    <div>
                                        <label className="block text-gray-700 font-medium mb-3">
                                            Backup Hours Needed
                                            <div className="text-2xl font-bold text-green-600 mt-1">{backupHours}h</div>
                                        </label>
                                        <input
                                            type="range"
                                            min="2"
                                            max="24"
                                            step="2"
                                            value={backupHours}
                                            onChange={(e) => setBackupHours(parseInt(e.target.value))}
                                            className="w-full h-2 bg-gray-300 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-4 [&::-webkit-slider-thumb]:border-green-500"
                                        />
                                        <div className="flex justify-between text-sm text-gray-500 mt-2">
                                            <span>2h</span>
                                            <span>24h</span>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* Maintenance Plan */}
                    <div>
                        <label className="block text-gray-700 font-medium mb-3">Maintenance Plan</label>
                        <div className="space-y-3">
                            {[
                                { id: 'basic', label: 'Basic', desc: 'Annual inspection', price: 'E1 500/yr' },
                                { id: 'premium', label: 'Premium', desc: 'Bi-annual check + cleaning', price: 'E3 000/yr' },
                                { id: 'extended', label: 'Extended', desc: 'Full maintenance + monitoring', price: 'E5 000/yr' }
                            ].map(plan => (
                                <label key={plan.id} className="block">
                                    <div className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${maintenancePlan === plan.id
                                        ? 'border-green-500 bg-green-50'
                                        : 'border-gray-300 hover:border-green-300'
                                        }`}>
                                        <input
                                            type="radio"
                                            name="maintenancePlan"
                                            value={plan.id}
                                            checked={maintenancePlan === plan.id}
                                            onChange={(e) => setMaintenancePlan(e.target.value)}
                                            className="h-5 w-5 text-green-600 mr-4"
                                        />
                                        <div className="flex-1">
                                            <div className="font-medium text-gray-800">{plan.label}</div>
                                            <div className="text-sm text-gray-600">{plan.desc}</div>
                                        </div>
                                        <div className="font-semibold text-green-600">
                                            {plan.price}
                                        </div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right Column - Results & Cost Breakdown */}
                <div className="space-y-6">
                    {/* System Summary */}
                    <div className="bg-green-50 p-6 rounded-xl border border-green-200">
                        <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                            <MdSolarPower className="mr-3 text-green-600" />
                            System Summary
                        </h3>

                        {/* Desired System Size — mode selector */}
                        <div className="bg-blue-50 p-4 rounded-lg mb-4 border-2 border-blue-100">
                            <div className="font-medium text-gray-800 mb-1">Desired System Size</div>
                            <div className="text-xs text-gray-500 mb-3">
                                Roof max: <span className="font-semibold text-green-600">{parseFloat(solarData.maxPossibleSystemSizeKW).toFixed(1)} kW</span>
                                {' '}· Bill-based auto: <span className="font-semibold text-blue-600">{calcAutoKW.toFixed(1)} kW</span>
                            </div>

                            {/* Radio buttons */}
                            <div className="flex flex-col gap-2 mb-3">
                                {[
                                    { id: 'auto', label: 'Auto (bill-based)', desc: 'Sized to your monthly electricity bill' },
                                    { id: 'roofMax', label: 'Use roof max', desc: `Fill all available roof space (${parseFloat(solarData.maxPossibleSystemSizeKW).toFixed(1)} kW)` },
                                    { id: 'manual', label: 'Manual entry', desc: 'Enter a specific system size in kW' },
                                ].map(opt => (
                                    <label key={opt.id} className="flex items-start gap-2 cursor-pointer group">
                                        <input
                                            type="radio"
                                            name="systemSizeMode"
                                            value={opt.id}
                                            checked={systemSizeMode === opt.id}
                                            onChange={() => {
                                                setSystemSizeMode(opt.id);
                                                if (opt.id === 'auto') {
                                                    setDesiredSystemSizeKW(null);
                                                    setDesiredSizeInput('');
                                                } else if (opt.id === 'roofMax') {
                                                    const v = Math.round(parseFloat(solarData.maxPossibleSystemSizeKW) * 10) / 10;
                                                    setDesiredSystemSizeKW(v);
                                                    setDesiredSizeInput(String(v));
                                                } else if (opt.id === 'manual') {
                                                    // Seed manual input with the current effective value
                                                    const seed = desiredSystemSizeKW ?? calcAutoKW;
                                                    setDesiredSystemSizeKW(seed);
                                                    setDesiredSizeInput(seed.toFixed(1));
                                                }
                                            }}
                                            className="mt-0.5 h-4 w-4 text-blue-600 accent-blue-600 shrink-0"
                                        />
                                        <div>
                                            <span className={`text-sm font-medium ${systemSizeMode === opt.id ? 'text-blue-700' : 'text-gray-700'}`}>{opt.label}</span>
                                            <span className="block text-xs text-gray-400">{opt.desc}</span>
                                        </div>
                                    </label>
                                ))}
                            </div>

                            {/* Manual entry controls — only shown when mode is manual */}
                            {systemSizeMode === 'manual' && (
                                <>
                                    <div className="flex items-center gap-3 mt-2">
                                        <input
                                            type="range"
                                            min="1"
                                            max={Math.max(1, desiredSystemSizeKW ? Math.ceil(desiredSystemSizeKW * 1.5) : Math.ceil(parseFloat(solarData.maxPossibleSystemSizeKW) * 1.5))}
                                            step="0.5"
                                            value={desiredSystemSizeKW !== null ? desiredSystemSizeKW : calcAutoKW}
                                            onChange={(e) => { const v = parseFloat(e.target.value); setDesiredSystemSizeKW(v); setDesiredSizeInput(String(v)); }}
                                            className="flex-1 h-3 bg-gradient-to-r from-blue-400 to-blue-600 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-4 [&::-webkit-slider-thumb]:border-blue-500 [&::-webkit-slider-thumb]:shadow-lg"
                                        />
                                        <div className="flex items-center border-2 border-blue-300 rounded-lg overflow-hidden shrink-0">
                                            <input
                                                type="number"
                                                min="1"
                                                step="0.5"
                                                value={desiredSizeInput}
                                                onFocus={() => {
                                                    if (desiredSizeInput === '') {
                                                        setDesiredSizeInput(calcAutoKW.toFixed(1));
                                                    }
                                                }}
                                                onChange={(e) => {
                                                    const raw = e.target.value;
                                                    setDesiredSizeInput(raw);
                                                    const val = parseFloat(raw);
                                                    if (!isNaN(val) && val > 0) {
                                                        setDesiredSystemSizeKW(val);
                                                    }
                                                }}
                                                onBlur={() => {
                                                    const val = parseFloat(desiredSizeInput);
                                                    if (isNaN(val) || val <= 0) {
                                                        setDesiredSystemSizeKW(null);
                                                        setDesiredSizeInput('');
                                                    } else {
                                                        setDesiredSystemSizeKW(val);
                                                        setDesiredSizeInput(String(val));
                                                    }
                                                }}
                                                className="w-20 px-2 py-2 text-center font-bold text-blue-700 bg-white focus:outline-none text-sm"
                                            />
                                            <span className="px-2 py-2 bg-blue-100 text-blue-600 font-medium text-sm">kW</span>
                                        </div>
                                    </div>
                                    <div className="flex justify-between text-xs text-gray-400 mt-1 px-0.5">
                                        <span>1 kW</span>
                                        <span>{parseFloat(solarData.maxPossibleSystemSizeKW).toFixed(1)} kW (roof max)</span>
                                    </div>
                                    {desiredSystemSizeKW && desiredSystemSizeKW <= parseFloat(solarData.maxPossibleSystemSizeKW) && (
                                        <div className="mt-2 text-xs text-blue-600 bg-blue-100 rounded px-2 py-1">
                                            ℹ️ Showing quote for {desiredSystemSizeKW} kW — {(parseFloat(solarData.maxPossibleSystemSizeKW) - desiredSystemSizeKW).toFixed(1)} kW of roof capacity unused
                                        </div>
                                    )}
                                </>
                            )}

                            {/* Roof-max info pill */}
                            {systemSizeMode === 'roofMax' && (
                                <div className="mt-1 text-xs text-green-700 bg-green-100 rounded px-2 py-1">
                                    ✅ System sized to fill your full {roofSize}m² — {parseFloat(solarData.maxPossibleSystemSizeKW).toFixed(1)} kW
                                </div>
                            )}
                        </div>

                        {/* Roof Space Info Section - UPDATED */}
                        <div className="bg-white p-4 rounded-lg mb-4 border-2 border-green-100">
                            <div className="flex justify-between items-center mb-3">
                                <div>
                                    <div className="font-medium text-gray-800">
                                        {roofType === 'ground' ? 'Ground Space Utilization' : 'Roof Space Utilization'}
                                    </div>
                                    <div className="text-sm text-gray-600">
                                        {solarData.usedRoofSpace}m² used of {roofSize}m² available
                                    </div>
                                </div>
                                <div className={`text-2xl font-bold ${solarData.roofUsagePercentage > 100 ? 'text-red-600' : 'text-green-600'}`}>{solarData.roofUsagePercentage}%</div>
                            </div>

                            {/* Progress Bar */}
                            <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden mb-3">
                                <div
                                    className={`h-full transition-all duration-500 ${solarData.roofUsagePercentage > 100 ? 'bg-gradient-to-r from-red-400 to-red-600' : 'bg-gradient-to-r from-green-400 to-green-600'}`}
                                    style={{ width: `${Math.min(solarData.roofUsagePercentage, 100)}%` }}
                                ></div>
                            </div>

                            {/* Status Messages */}
                            {solarData.roofUsagePercentage > 100 ? (
                                <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg border border-red-200">
                                    <div className="font-medium mb-1">⚠️ Insufficient {roofType === 'ground' ? 'Ground' : 'Roof'} Space</div>
                                    <div className="text-red-700">
                                        This system needs <strong>{solarData.usedRoofSpace}m²</strong> of panel space but only <strong>{roofSize}m²</strong> is available.
                                        The DC oversizing ratio ({systemType === 'grid-tie' ? '1.5×' : '1×'}) requires more physical panels than the roof can fit.
                                        Consider increasing your {roofType === 'ground' ? 'ground area' : 'roof space'} to at least {solarData.usedRoofSpace}m², or reduce the system size.
                                    </div>
                                </div>
                            ) : solarData.isRoofLimited ? (
                                // User explicitly requested MORE than the roof can fit
                                desiredSystemSizeKW && desiredSystemSizeKW > parseFloat(solarData.maxPossibleSystemSizeKW) ? (
                                    <div className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg border border-amber-200">
                                        <div className="font-medium mb-1">⚠️ {roofType === 'ground' ? 'Ground Space' : 'Roof Space'} Limiting System Size</div>
                                        <div className="text-amber-700">
                                            Your {roofType === 'ground' ? 'ground' : 'roof'} can only fit a {solarData.maxPossibleSystemSizeKW}kW system,
                                            but you have requested {desiredSystemSizeKW}kW. The system has been capped at the roof maximum.
                                            {roofSize < 50 ? ` Consider expanding your ${roofType === 'ground' ? 'ground' : 'roof'} area.` : ''}
                                        </div>
                                    </div>
                                ) : (
                                    // Auto mode or desired size within roof capacity — roof is the natural ceiling
                                    <div className="text-sm text-blue-600 bg-blue-50 p-3 rounded-lg border border-blue-200">
                                        <div className="font-medium mb-1">ℹ️ System Sized to {roofType === 'ground' ? 'Ground' : 'Roof'} Maximum</div>
                                        <div className="text-blue-700">
                                            Your {roofType === 'ground' ? 'ground' : 'roof'} fits up to {solarData.maxPossibleSystemSizeKW}kW — the system is using all available space.
                                            Your full energy need of {solarData.requiredSystemSizeKW}kW would require a larger {roofType === 'ground' ? 'ground area' : 'roof'}.
                                        </div>
                                    </div>
                                )
                            ) : solarData.roofUsagePercentage < 80 ? (
                                <div className="text-sm text-blue-600 bg-blue-50 p-3 rounded-lg border border-blue-200">
                                    <div className="font-medium mb-1">📈 Room for Expansion</div>
                                    <div className="text-blue-700">
                                        You have space for {solarData.extraPanelsPossible} more panels
                                        ({solarData.extraRoofSpaceAvailable}m² available).
                                    </div>
                                </div>
                            ) : (
                                <div className="text-sm text-green-600 bg-green-50 p-3 rounded-lg border border-green-200">
                                    <div className="font-medium mb-1">✅ Optimal {roofType === 'ground' ? 'Ground' : 'Roof'} Utilization</div>
                                    <div className="text-green-700">
                                        Your {roofType === 'ground' ? 'ground' : 'roof'} space is efficiently utilized for your energy needs.
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-6">
                            <div className="bg-white p-4 rounded-lg text-center">
                                <div className="text-3xl font-bold text-green-600">{solarData.systemSizeKW}</div>
                                <div className="text-sm text-gray-600 mt-1">System Size (kW)</div>
                            </div>
                            <div className="bg-white p-4 rounded-lg text-center">
                                <div className="text-3xl font-bold text-green-600">{solarData.panelCount}</div>
                                <div className="text-sm text-gray-600 mt-1">Solar Panels</div>
                            </div>
                            <div className="bg-white p-4 rounded-lg text-center">
                                <div className="text-3xl font-bold text-green-600">{solarData.dailyProduction}</div>
                                <div className="text-sm text-gray-600 mt-1">Daily Production</div>
                            </div>
                            <div className="bg-white p-4 rounded-lg text-center">
                                <div className="text-3xl font-bold text-green-600">{solarData.coveragePercentage}%</div>
                                <div className="text-sm text-gray-600 mt-1">Energy Coverage</div>
                            </div>
                        </div>

                        {/* Cost Breakdown */}
                        <div className="space-y-3 mb-6">
                            <h4 className="font-bold text-gray-700">Detailed Cost Breakdown</h4>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span>Solar Panels ({solarData.panelCount} × 600W)</span>
                                    <span className="font-semibold">E{fmtNum(solarData.costBreakdown.panels)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>{solarData.inverterType} ({solarData.inverterSize} kW)</span>
                                    <span className="font-semibold">E{fmtNum(solarData.costBreakdown.inverter)}</span>
                                </div>
                                {solarData.batterySize > 0 && (
                                    <div className="flex justify-between">
                                        <span>Lithium Batteries ({solarData.batterySize} kWh)</span>
                                        <span className="font-semibold">E{fmtNum(solarData.costBreakdown.batteries)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between">
                                    <span>Mounting Structure ({roofType === 'ground' ? 'Ground Mount' : roofType})</span>
                                    <span className="font-semibold">E{fmtNum(solarData.costBreakdown.mounting)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Electrical Components</span>
                                    <span className="font-semibold">E{fmtNum(solarData.costBreakdown.electrical)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Installation Labor</span>
                                    <span className="font-semibold">E{fmtNum(solarData.costBreakdown.labor)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Transport</span>
                                    <span className="font-semibold">E{fmtNum(solarData.costBreakdown.misc)}</span>
                                </div>

                                {/* ADD MAINTENANCE AS SEPARATE RECURRING COST */}
                                <div className="border-t pt-3 mt-2 border-blue-200">
                                    <div className="flex justify-between items-center mb-1">
                                        <span className="text-blue-600 font-medium">Annual Maintenance Plan ({maintenancePlan})</span>
                                        <span className="font-semibold text-blue-600">E{fmtNum(solarData.costBreakdown.maintenance)}/year</span>
                                    </div>
                                    <div className="text-xs text-gray-500 italic">
                                        Optional annual service (billed separately, not included in total below)
                                    </div>
                                </div>

                                <div className="border-t pt-2 mt-2">
                                    <div className="flex justify-between font-semibold">
                                        <span>Equipment & Installation Subtotal</span>
                                        <span>E{fmtNum(solarData.costBreakdown.subtotal)}</span>
                                    </div>
                                    <div className="flex justify-between text-orange-600 font-medium">
                                        <span>VAT (15%)</span>
                                        <span>E{fmtNum(solarData.costBreakdown.vat)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Final Pricing */}
                        <div className="space-y-4">
                            <div className="flex justify-between items-center p-4 bg-gradient-to-r from-green-500 to-green-600 rounded-lg text-white">
                                <div>
                                    <div className="text-lg font-bold">Total Investment</div>
                                    <div className="text-xs opacity-80">Including VAT</div>
                                </div>
                                <div className="text-3xl font-bold">E{fmtNum(solarData.totalCost)}</div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-white p-4 rounded-lg text-center">
                                    <div className="text-xl font-bold text-green-600">E{fmtNum(solarData.monthlySavings)}</div>
                                    <div className="text-sm text-gray-600">Monthly Savings</div>
                                </div>
                                <div className="bg-white p-4 rounded-lg text-center">
                                    <div className="text-xl font-bold text-green-600">{solarData.paybackYears} yrs</div>
                                    <div className="text-sm text-gray-600">Payback Period</div>
                                </div>
                            </div>

                            <div className="bg-white p-4 rounded-lg">
                                <div className="flex justify-between items-center mb-2">
                                    <div className="text-gray-700">Annual Savings</div>
                                    <div className="text-lg font-bold text-green-600">E{fmtNum(solarData.annualSavings)}</div>
                                </div>
                                <div className="flex justify-between items-center">
                                    <div className="text-gray-700">25-Year ROI</div>
                                    <div className="text-lg font-bold text-green-600">{solarData.roiPercentage}%</div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                            <div className="text-sm text-yellow-800">
                                <strong>✅ What's Included:</strong> Full installation, system design, 5-year workmanship warranty, 25-year panel warranty, 10-year inverter warranty{solarData.batterySize > 0 ? ', 10-year battery warranty' : ''}.
                                <div className="mt-2">
                                    <strong>📋 Optional Maintenance:</strong> {maintenancePlan === 'basic' ? 'Annual inspection' : maintenancePlan === 'premium' ? 'Bi-annual check + cleaning' : 'Full maintenance + monitoring'} available at E{fmtNum(solarData.costBreakdown.maintenance)}/year.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Contact Info Banner */}
            <div className="mt-8 p-5 bg-green-50 border border-green-200 rounded-xl flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex items-center gap-2 text-green-700 font-semibold text-sm shrink-0">
                    <MdSolarPower className="text-xl" />
                    Need help? Contact us directly:
                </div>
                <div className="flex flex-col sm:flex-row gap-3 text-sm">
                    <a
                        href="tel:+26825185414"
                        className="flex items-center gap-2 text-green-800 font-medium hover:text-green-600 transition-colors"
                    >
                        <MdPhone className="text-base shrink-0" />
                        +268 2518 5414
                    </a>
                    <a
                        href="tel:+26825187424"
                        className="flex items-center gap-2 text-green-800 font-medium hover:text-green-600 transition-colors"
                    >
                        <MdPhone className="text-base shrink-0" />
                        +268 2518 7424
                    </a>
                    <a
                        href="mailto:info@alternative.co.sz"
                        className="flex items-center gap-2 text-green-800 font-medium hover:text-green-600 transition-colors"
                    >
                        <MdEmail className="text-base shrink-0" />
                        info@alternative.co.sz
                    </a>
                </div>
            </div>
        </div>
    );
};

export default SolarCalculatorSection;